import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import "./NewsroomDetailsPage.css";
import { getNewsroomShareholderLetters } from "../services/shareholderLetterService";

const lettersHeroTitleWords = ["Letters", "from", "the", "CEO"];



const formatLetterDate = (letter) => {
  const rawDate = letter?.publishedAt || letter?.updatedAt || letter?.createdAt;

  if (!rawDate) {
    return "";
  }

  const parsed = new Date(rawDate);

  if (Number.isNaN(parsed.getTime())) {
    return "";
  }

  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric"
  }).format(parsed);
};

const isPublishedLetter = (letter) =>
  letter?.status?.toString().trim().toUpperCase() === "PUBLISHED";




const sectionContent = {
  
  letters: {
    title: "Letters from the CEO",
    heroClass: "details-hero--letters",
  },
  
};

const InlineAction = ({ to = "/help", state, children }) => (
  <Link to={to} state={state} className="details-inline-link">
    <span>{children}</span>
    <span>+</span>
  </Link>
);



const LettersSection = ({ letters, lettersError }) => (
  <section className="letters-section">
    <h2>Recent letters</h2>
    <div className="letters-grid">
      {letters.map((letter) => (
        <article key={letter.id || letter.languageSlug} className="letters-card">
          <p className="letters-date">{formatLetterDate(letter)}</p>
          <h3>Letter to Shareholders</h3>
          <p className="letters-copy">
            {letter.title || letter.languageLabel || "Shareholder update"}
          </p>
          <InlineAction
            to="/letter-to-shareholders"
            state={{ selectedLetter: letter, source: "newsroom" }}
          >
            Read More
          </InlineAction>
        </article>
      ))}

      {!letters.length && (
        <p className="letters-empty-state">
          {lettersError || "No shareholder letters have been created yet."}
        </p>
      )}
    </div>
  </section>
);



const NewsroomDetailsPage = () => {
  const location = useLocation();
  const [isReady, setIsReady] = useState(false);
  const [shareholderLetters, setShareholderLetters] = useState([]);
  const [lettersError, setLettersError] = useState("");

  const section = useMemo(
    () => new URLSearchParams(location.search).get("section") || "media",
    [location.search]
  );

  const currentSection = sectionContent[section] || sectionContent.media;

  useEffect(() => {
    const timer = window.setTimeout(() => setIsReady(true), 80);
     window.scrollTo(0, 0);
    return () => window.clearTimeout(timer);
  }, [section]);

  useEffect(() => {
    if (section !== "letters") {
      return;
    }

    const loadLetters = async () => {
      try {
        setLettersError("");
        const res = await getNewsroomShareholderLetters();
        const payload = Array.isArray(res?.data) ? res.data : [];
        setShareholderLetters(payload.filter(isPublishedLetter));
      } catch (error) {
        console.error("Failed to load newsroom shareholder letters:", error);
        setShareholderLetters([]);
        setLettersError("Unable to load shareholder letters right now.");
      }
    };

    loadLetters();
  }, [section]);

  return (
    <main className="newsroom-details-wrapper">
      <div className="newsroom-details-container">
        <div className="newsroom-details-back">
          <Link to="/newsroom">&larr; Back to Newsroom</Link>
        </div>

        <section className={`details-hero ${currentSection.heroClass} ${isReady ? "is-ready" : ""}`.trim()}>
          <div>
            {section === "letters" ? (
              <h1 className="details-hero-title details-hero-title--animated">
                <span
                  className={`details-hero-title-text details-hero-title-text-stacked${isReady ? " is-visible" : ""}`}
                  aria-label={currentSection.title}
                >
                  <span className="details-hero-title-line">
                    {lettersHeroTitleWords.slice(0, 3).map((word, wordIndex) => {
                      const letterOffset = lettersHeroTitleWords
                        .slice(0, wordIndex)
                        .reduce((count, currentWord) => count + currentWord.length, 0);

                      return (
                        <span key={word} className="details-hero-title-word">
                          {word.split("").map((letter, letterIndex) => (
                            <span
                              key={`${word}-${letterIndex}`}
                              className="details-hero-title-letter"
                              style={{ "--reveal-order": letterOffset + letterIndex + 1 }}
                            >
                              {letter}
                            </span>
                          ))}
                        </span>
                      );
                    })}
                  </span>

                  <span className="details-hero-title-line">
                    {lettersHeroTitleWords.slice(3).map((word, wordIndex) => {
                      const letterOffset = lettersHeroTitleWords
                        .slice(0, 3)
                        .reduce((count, currentWord) => count + currentWord.length, 0) + wordIndex;

                      return (
                        <span key={word} className="details-hero-title-word">
                          {word.split("").map((letter, letterIndex) => (
                            <span
                              key={`${word}-${letterIndex}`}
                              className="details-hero-title-letter"
                              style={{ "--reveal-order": letterOffset + letterIndex + 1 }}
                            >
                              {letter}
                            </span>
                          ))}
                        </span>
                      );
                    })}
                  </span>
                </span>
              </h1>
            ) : (
              <h1 className="details-hero-title">{currentSection.title}</h1>
            )}
          </div>

          <div className="details-hero-side">
            <p>{currentSection.summary}</p>
            {section === "thought-leadership" ? (
              <div className="thought-topic-links">
                <InlineAction>AI strategy</InlineAction>
                <InlineAction>Security</InlineAction>
                <InlineAction>Design</InlineAction>
                <InlineAction>Leadership</InlineAction>
              </div>
            ) : null}
            {section === "media" ? (
              <InlineAction>Download overview</InlineAction>
            ) : null}
            {section === "press-releases" ? (
              <InlineAction>Media contact</InlineAction>
            ) : null}
          </div>
        </section>

       
        {section === "letters" && (
          <LettersSection letters={shareholderLetters} lettersError={lettersError} />
        )}
       
      </div>
    </main>
  );
};

export default NewsroomDetailsPage;
